import React, { memo } from 'react';
import { CheckCircle, Layers, Target, Users, MessageCircle, TrendingUp, ShieldCheck, BarChart } from 'lucide-react';
export function ModulesSection() {
  return <section className="py-20 px-4 bg-white" data-id="element-128">
      <div className="max-w-6xl mx-auto" data-id="element-129">
        <div className="text-center mb-16" data-id="element-130">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4" data-id="element-131">
            Cómo está estructurado el material
          </h2>
          <p className="text-xl text-gray-600" data-id="element-132">
            12 módulos anuales • De menor a mayor complejidad
          </p>
        </div>

        {/* Structure Info */}
        <div className="grid md:grid-cols-2 gap-8 mb-16" data-id="element-133">
          <div className="bg-blue-50 p-8 rounded-xl border border-blue-100" data-id="element-134">
            <h3 className="font-bold text-blue-900 mb-4 text-lg" data-id="element-135">
              ✔ Cada módulo incluye:
            </h3>
            <ul className="space-y-3" data-id="element-136">
              <li className="flex items-center gap-2 text-gray-700" data-id="element-137">
                <div className="w-2 h-2 bg-blue-500 rounded-full" data-id="element-138"></div>
                Objetivos claros
              </li>
              <li className="flex items-center gap-2 text-gray-700" data-id="element-139">
                <div className="w-2 h-2 bg-blue-500 rounded-full" data-id="element-140"></div>
                Contenidos explicados con lenguaje didáctico
              </li>
              <li className="flex items-center gap-2 text-gray-700" data-id="element-141">
                <div className="w-2 h-2 bg-blue-500 rounded-full" data-id="element-142"></div>
                Actividades prácticas
              </li>
              <li className="flex items-center gap-2 text-gray-700" data-id="element-143">
                <div className="w-2 h-2 bg-blue-500 rounded-full" data-id="element-144"></div>
                Instrumentos de evaluación
              </li>
            </ul>
          </div>

          <div className="bg-orange-50 p-8 rounded-xl border border-orange-100" data-id="element-145">
            <h3 className="font-bold text-orange-900 mb-4 text-lg" data-id="element-146">
              📍 Distribución sugerida:
            </h3>
            <div className="space-y-4" data-id="element-147">
              <div className="flex items-center justify-between bg-white p-3 rounded-lg shadow-sm" data-id="element-148">
                <span className="font-medium text-gray-700" data-id="element-149">
                  1° Cuatrimestre
                </span>
                <span className="font-bold text-orange-600" data-id="element-150">Módulos 1 a 6</span>
              </div>
              <div className="flex items-center justify-between bg-white p-3 rounded-lg shadow-sm" data-id="element-151">
                <span className="font-medium text-gray-700" data-id="element-152">
                  2° Cuatrimestre
                </span>
                <span className="font-bold text-orange-600" data-id="element-153">
                  Módulos 7 a 12
                </span>
              </div>
              <p className="text-sm text-gray-600 mt-2 italic" data-id="element-154">
                Todo pensado para que el año tenga orden, coherencia y
                continuidad.
              </p>
            </div>
          </div>
        </div>

        {/* Modules Grid */}
        <h3 className="text-2xl font-bold text-gray-900 mb-8 border-l-4 border-blue-600 pl-4" data-id="element-155">
          Recorrido completo por los módulos
        </h3>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-20" data-id="element-156">
          <ModuleCard number={1} title="Introducción a las Organizaciones" desc="Qué es una organización, tipos e importancia social." activity="Análisis de organizaciones reales del entorno." data-id="element-157" />
          <ModuleCard number={2} title="Evolución de la Teoría Organizacional" desc="Taylor, Fayol y Mayo." activity="Línea de tiempo + síntesis guiada." data-id="element-158" />
          <ModuleCard number={3} title="Estructura Organizacional" desc="División del trabajo, jerarquía y organigramas." activity="Diseño de una empresa ficticia con evaluación por rúbrica." data-id="element-159" />
          <ModuleCard number={4} title="Cultura y Clima Organizacional" desc="Valores, normas y clima laboral." activity="Encuestas internas simuladas + informe." data-id="element-160" />
          <ModuleCard number={5} title="Motivación y Liderazgo" desc="Maslow, Herzberg y estilos de liderazgo." activity="Simulación de decisiones + autoevaluación." data-id="element-161" />
          <ModuleCard number={6} title="Comunicación Organizacional" desc="Tipos, barreras y canales." activity="Juego de roles y análisis de casos reales." data-id="element-162" />
          <ModuleCard number={7} title="Toma de Decisiones" desc="Proceso y resolución de problemas." activity="Caso real + presentación grupal." data-id="element-163" />
          <ModuleCard number={8} title="Grupos y Equipos de Trabajo" desc="Diferencias, fases y roles." activity="Proyecto colaborativo completo." data-id="element-164" />
          <ModuleCard number={9} title="Cambio Organizacional" desc="Factores, resistencias y estrategias." activity="Plan de cambio para una organización local." data-id="element-165" />
          <ModuleCard number={10} title="Ética y Responsabilidad Social" desc="Dilemas éticos e impacto social." activity="Debate argumentativo guiado." data-id="element-166" />
          <ModuleCard number={11} title="Sistemas de Control" desc="Indicadores, seguimiento y control." activity="Diseño de métricas organizacionales." data-id="element-167" />
          <ModuleCard number={12} title="Proyecto Final Integrador" desc="Los alumnos analizan, diagnostican y proponen mejoras." activity="Incluye evaluación completa y defensa oral." highlight data-id="element-168" />
        </div>

        {/* Competencies */}
        <div className="bg-gray-900 text-white rounded-2xl p-8 md:p-16 text-center" data-id="element-169">
          <h3 className="text-2xl md:text-3xl font-bold mb-8" data-id="element-170">
            Las competencias que realmente desarrolla el alumno
          </h3>
          <p className="text-lg text-gray-300 mb-10 max-w-2xl mx-auto" data-id="element-171">
            Estas clases no forman memoria. Forman criterio. Aprenden a pensar
            organizaciones, no solo a repetir conceptos.
          </p>

          <div className="flex flex-wrap justify-center gap-4" data-id="element-172">
            <CompetencyTag text="Pensamiento organizacional" data-id="element-173" />
            <CompetencyTag text="Capacidad de análisis" data-id="element-174" />
            <CompetencyTag text="Trabajo en equipo" data-id="element-175" />
            <CompetencyTag text="Comunicación efectiva" data-id="element-176" />
            <CompetencyTag text="Ética y responsabilidad social" data-id="element-177" />
          </div>
        </div>
      </div>
    </section>;
}
function ModuleCard({
  number,
  title,
  desc,
  activity,
  highlight = false
}: {
  number: number;
  title: string;
  desc: string;
  activity: string;
  highlight?: boolean;
}) {
  return <div className={`relative p-6 rounded-xl border transition-all duration-300 hover:shadow-lg ${highlight ? 'bg-blue-50 border-blue-200 ring-2 ring-blue-100' : 'bg-white border-gray-200'}`} data-id="element-178">
      <div className="absolute -top-3 -left-3 w-10 h-10 bg-blue-600 text-white rounded-lg flex items-center justify-center font-bold shadow-md" data-id="element-179">
        {number}
      </div>
      <div className="mt-4" data-id="element-180">
        <h4 className="font-bold text-gray-900 text-lg mb-2 leading-tight" data-id="element-181">
          {title}
        </h4>
        <p className="text-gray-600 text-sm mb-4" data-id="element-182">{desc}</p>
        <div className="bg-gray-50 p-3 rounded-lg border border-gray-100" data-id="element-183">
          <span className="text-xs font-bold text-orange-600 uppercase tracking-wide block mb-1" data-id="element-184">
            Actividad:
          </span>
          <p className="text-sm text-gray-700" data-id="element-185">{activity}</p>
        </div>
      </div>
    </div>;
}
function CompetencyTag({
  text
}: {
  text: string;
}) {
  return <span className="px-4 py-2 bg-white/10 rounded-full text-sm md:text-base font-medium border border-white/20 backdrop-blur-sm" data-id="element-186">
      {text}
    </span>;
}